import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePortalApi } from "../api/ApiContext";
import { useAuth } from "../auth/AuthContext";
import type { SessionUser, TenantMembership } from "../api/types";

// Guardado em sessionStorage (nunca localStorage): apenas o id do tenant
// escolhido, que só é honrado se ainda constar nos vínculos da sessão.
const ACTIVE_TENANT_KEY = "ava-portal.active-tenant.v1";

export type TenantContextValue = {
  sessionStatus: "idle" | "loading" | "error" | "ready";
  sessionError: string | null;
  user: SessionUser | null;
  memberships: TenantMembership[];
  activeMembership: TenantMembership | null;
  // Só aceita tenants presentes nos vínculos; retorna false caso contrário.
  selectTenant: (tenantId: string) => boolean;
  // Volta para a seleção de condomínio limpando o cache.
  clearActiveTenant: () => void;
  // Limpa cache e armazenamento sensível e encerra a sessão.
  signOut: () => void;
  refetchSession: () => void;
};

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const api = usePortalApi();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [storedTenantId, setStoredTenantId] = useState<string | null>(() =>
    sessionStorage.getItem(ACTIVE_TENANT_KEY),
  );

  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => api.getSession(),
    enabled: auth.status === "signed_in",
    staleTime: 5 * 60 * 1000,
  });

  const memberships = useMemo(() => session.data?.memberships ?? [], [session.data]);

  // O tenant ativo nunca vem de URL nem de valor arbitrário: precisa estar
  // entre os vínculos retornados por /tenant/session.
  const activeMembership = useMemo(() => {
    if (!memberships.length) return null;
    const stored = memberships.find((m) => m.tenant_id === storedTenantId);
    if (stored) return stored;
    if (memberships.length === 1) return memberships[0];
    return null;
  }, [memberships, storedTenantId]);

  const selectTenant = useCallback(
    (tenantId: string): boolean => {
      const membership = memberships.find((m) => m.tenant_id === tenantId);
      if (!membership) return false;
      // Troca de tenant zera o cache: nenhum dado de um condomínio pode ser
      // reaproveitado em outro.
      queryClient.clear();
      sessionStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
      setStoredTenantId(tenantId);
      return true;
    },
    [memberships, queryClient],
  );

  const clearActiveTenant = useCallback(() => {
    queryClient.clear();
    sessionStorage.removeItem(ACTIVE_TENANT_KEY);
    setStoredTenantId(null);
  }, [queryClient]);

  const signOut = useCallback(() => {
    queryClient.clear();
    sessionStorage.removeItem(ACTIVE_TENANT_KEY);
    setStoredTenantId(null);
    auth.signOut();
  }, [queryClient, auth]);

  const value = useMemo<TenantContextValue>(
    () => ({
      sessionStatus:
        auth.status !== "signed_in"
          ? "idle"
          : session.isPending
            ? "loading"
            : session.isError
              ? "error"
              : "ready",
      sessionError: session.isError
        ? session.error instanceof Error
          ? session.error.message
          : "Falha ao carregar a sessão."
        : null,
      user: session.data?.user ?? null,
      memberships,
      activeMembership,
      selectTenant,
      clearActiveTenant,
      signOut,
      refetchSession: () => void session.refetch(),
    }),
    [auth.status, session, memberships, activeMembership, selectTenant, clearActiveTenant, signOut],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (!value) throw new Error("useTenant precisa estar dentro de um TenantProvider.");
  return value;
}

// Para telas internas, onde o tenant ativo é garantido pelo PortalShell.
export function useActiveTenant(): TenantMembership {
  const { activeMembership } = useTenant();
  if (!activeMembership) throw new Error("Nenhum condomínio ativo selecionado.");
  return activeMembership;
}
